/** Shared helpers for counts and rates on the admin pages. */
export function pct(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

/** n ÷ d, or null when there's nothing to divide by (render as "—", not 0%). */
export function ratio(n: number, d: number): number | null {
  return d > 0 ? n / d : null;
}

/** Invite statuses to open from a count, by what the count measures. */
export const COUNT_STATUSES = {
  logged: ['PENDING', 'VERIFIED', 'CONVERTED', 'REJECTED'],
  joined: ['VERIFIED', 'CONVERTED'],
  posted: ['CONVERTED'],
  replied: ['PENDING'],
} as const;

/**
 * Link from a count to the invites list it summarises:
 * `/admin/invites?inviterId=12&statuses=VERIFIED,CONVERTED`.
 *
 * `dateFrom` / `dateTo` are `YYYY-MM-DD` days (what the Invites page's date
 * inputs hold) and filter on when the invite was logged; `convertedFrom/To`
 * and `verifiedFrom/To` filter on when it posted / joined instead (for
 * "what happened in this period" counts). `stages` sends the page's own
 * `?stage=` list, which wins over `statuses`. `view: 'contacted'` opens the
 * "Messaged · no reply" list.
 */
export function invitesListHref(
  opts: {
    inviterId?: number | null;
    statuses?: readonly string[];
    base?: string;
    team?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
    convertedFrom?: string | null;
    convertedTo?: string | null;
    verifiedFrom?: string | null;
    verifiedTo?: string | null;
    stages?: readonly string[];
    channel?: string | null;
    niche?: string | null;
    source?: string | null;
    owed?: boolean;
    view?: 'contacted' | null;
  } = {},
): string {
  const params = new URLSearchParams();
  if (opts.view) params.set('view', opts.view);
  if (opts.team) params.set('team', opts.team);
  if (opts.dateFrom) params.set('dateFrom', opts.dateFrom);
  if (opts.dateTo) params.set('dateTo', opts.dateTo);
  if (opts.convertedFrom) params.set('convertedFrom', opts.convertedFrom);
  if (opts.convertedTo) params.set('convertedTo', opts.convertedTo);
  if (opts.verifiedFrom) params.set('verifiedFrom', opts.verifiedFrom);
  if (opts.verifiedTo) params.set('verifiedTo', opts.verifiedTo);
  if (opts.inviterId != null) params.set('inviterId', String(opts.inviterId));
  if (opts.channel) params.set('channel', opts.channel);
  if (opts.niche) params.set('niche', opts.niche);
  if (opts.source) params.set('source', opts.source);
  if (opts.owed) params.set('owed', '1');
  // AdminInvites reads a comma-separated `stage` list, or the older `status` one.
  if (opts.stages && opts.stages.length > 0) params.set('stage', opts.stages.join(','));
  else if (opts.statuses && opts.statuses.length > 0) params.set('status', opts.statuses.join(','));
  const qs = params.toString().replace(/%2C/g, ',');
  return `${opts.base ?? '/admin/invites'}${qs ? `?${qs}` : ''}`;
}
